import {
  Box,
  Checkbox,
  CloseButton,
  Dialog,
  IconButton,
  ListMenu,
  pn,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@compass/core";

import EventNoteIcon from "@mui/icons-material/EventNote";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import { ListBeProperty } from "components";
import {
  LkRoleId,
  Permission,
  PermissionAction,
  SortObject,
  StudentListDto,
  StudentListResource,
  TenantInfo,
} from "generated/openApi";
import { studentServicePageRoute } from "pages/students/student-services/StudentServicePage/StudentServicePage";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "redux/store";
import { isAuthorized } from "services";
import { studentPageRoute } from "../StudentPage/StudentPage";
import { useStudentListMenuItems, WalletPassResults } from "./useStudentListMenuItems";

const WalletPassResultsDialog = (props: {
  results: WalletPassResults | undefined;
  onClose: () => void;
}) => {
  if (!props.results) return null;

  const summary =
    props.results.sent === 0
      ? "No Mobile Passes were sent."
      : `Successfully sent ${props.results.sent} of ${props.results.total} ${
          props.results.sent === 1 ? "Mobile Pass" : "Mobile Passes"
        }.`;
  const studentLabel = props.results.errorMessages.length === 1 ? "student" : "students";

  return (
    <Dialog
      open
      onClose={props.onClose}
      maxWidth="sm"
      header={
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="inherit">Mobile Pass Results</Typography>
          <CloseButton theme="Dark" onClick={props.onClose} />
        </Stack>
      }
      content={
        <Stack spacing="md">
          <Typography variant="body1">{summary}</Typography>
          <Typography variant="subtitle1">Failed to send to the following {studentLabel}:</Typography>
          <Box sx={{ maxHeight: "300px", overflowY: "auto" }}>
            <Box component="ul">
              {props.results.errorMessages.map((message, index) => (
                <Typography component="li" color="error" variant="caption" key={`${index}-${message}`}>
                  {message}
                </Typography>
              ))}
            </Box>
          </Box>
        </Stack>
      }
    />
  );
};

export const StudentTable = (props: {
  rootPermission: Permission;
  tenantTextingInfo: TenantInfo | undefined;
  tableContainerRef: React.RefObject<HTMLDivElement>;
  students: StudentListDto[];
  listTotal: number | undefined;
  resource: StudentListResource;
  searchFocused: boolean;
  sortObjects: SortObject[] | undefined;
  sort: (sortObjects: SortObject[]) => void;
  selectId: (id: number) => void;
  selectedIds: number[];
  setSelectedIds: React.Dispatch<React.SetStateAction<number[]>>;
  getIdsPromise: () => Promise<any>;
  loadList: () => void;
}) => {
  const navigate = useNavigate();
  const properties = props.resource.properties || [];
  const [walletPassResults, setWalletPassResults] = useState<WalletPassResults>();
  const menuItems = useStudentListMenuItems({
    tenantTextingInfo: props.tenantTextingInfo,
    permission: props.rootPermission,
    selectedIds: props.selectedIds,
    clearSelectedIds: () => props.setSelectedIds([]),
    showWalletPassResults: setWalletPassResults,
  });
  const roleId = useSelector((state: RootState) => state.app.roleId);

  const view = (item: StudentListDto) => {
    if (roleId === LkRoleId.DataEntry) return undefined;
    return () => navigate(studentPageRoute.getPath({ id: item.id, crudMode: "view" }));
  };

  return (
    <TableContainer ref={props.tableContainerRef}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {menuItems.length > 0 && (
              <TableCell type="select">
                <ListMenu
                  selectedIds={props.selectedIds}
                  setSelectedIds={props.setSelectedIds}
                  listTotal={props.listTotal}
                  fetchFunction={() => props.getIdsPromise()}
                  actionMenuItems={menuItems}
                />
              </TableCell>
            )}

            {props.resource.customIdTypes?.map((customIdType) => (
              <TableCell key={customIdType.id}>
                <ListBeProperty
                  listProperty={properties?.[`customId${customIdType.id}}`]}
                  displayName={customIdType.name}
                  sortObjects={props.sortObjects}
                  onChange={(sortObjects) => props.sort(sortObjects)}
                  searchFocused={props.searchFocused}
                />
              </TableCell>
            ))}

            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<StudentListDto>("fullName"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<StudentListDto>("currentSchool"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell align="center">
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<StudentListDto>("currentGradeLevel"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.students.map((studentItem) => (
            <TableRow key={studentItem.id} onClick={view(studentItem)}>
              {menuItems.length > 0 && (
                <TableCell type="select">
                  <Checkbox
                    checked={props.selectedIds.includes(studentItem.id)}
                    onClick={(e) => props.selectId(studentItem.id)}
                  />
                </TableCell>
              )}

              {props.resource.customIdTypes?.map((customIdType) => (
                <TableCell key={customIdType.id}>
                  <Typography
                    color={
                      customIdType.id === 1 && studentItem[`customId${customIdType.id}`]?.startsWith("X-")
                        ? "error"
                        : undefined
                    }
                    variant="inherit"
                  >
                    {studentItem[`customId${customIdType.id}`]}
                  </Typography>
                </TableCell>
              ))}

              <TableCell>
                {studentItem.fullName}{" "}
                {studentItem.middleName &&
                  studentItem.middleName.length > 0 &&
                  studentItem.middleName.charAt(0).toUpperCase() + "."}
              </TableCell>
              <TableCell>{studentItem.currentSchool}</TableCell>
              <TableCell align="center">{studentItem.currentGradeLevel}</TableCell>
              <TableCell type="action">
                {isAuthorized(Permission.StudentService, PermissionAction.Create) && (
                  <IconButton
                    onClick={() =>
                      navigate({
                        pathname: studentServicePageRoute.getPath({ id: 0, crudMode: "edit", screenType: "dialog" }),
                        search: studentServicePageRoute.getOptionalParams?.({
                          hideStudent: true,
                          studentId: studentItem.id,
                        }),
                      })
                    }
                    color="primary"
                    size="small"
                  >
                    <Stack direction="row" alignItems="center">
                      <Typography variant="caption">+</Typography>
                      <EventNoteIcon />
                    </Stack>
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <WalletPassResultsDialog results={walletPassResults} onClose={() => setWalletPassResults(undefined)} />
    </TableContainer>
  );
};
